import type { Cadence } from './reportEnums.constants';

/** Warehouse metric name for VBP eligible / engaged counts (`actual_numerator` / `actual_denominator`). */
export const VBP_ENGAGEMENT_RATE_METRIC = 'VBP Engagement Rate' as const;

/** Warehouse `metric` values for VBP population counts. Assigned is stored but not shown. */
export const VBP_ELIGIBLE_METRIC = 'VBP Eligible' as const;
export const VBP_ASSIGNED_METRIC = 'VBP Assigned' as const;
export const VBP_ENGAGED_METRIC = 'VBP Engaged' as const;

/** Funnel bar labels in display order (eligible → engaged). */
export const VBP_FUNNEL_STEP_LABELS = [
  'VBP eligible',
  'VBP engaged',
] as const;

/**
 * Normalizes a measurement period label to match BigQuery `measurement_period`
 * (e.g. `202604 - 202703` with spaced hyphen).
 * @param label - Stored report `reportingPeriod` or customer option label.
 * @returns Trimmed label with consistent ` - ` separator.
 */
export const normalizeWarehouseMeasurementPeriod = (label: string): string => {
  const trimmed = label.trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.replace(/\s*[-–—]\s*/g, ' - ');
};

/**
 * Parses the end `YYYYMM` from a warehouse measurement period label.
 * @param label - e.g. `202604 - 202703`.
 * @returns Six-digit end month or null when not parseable.
 */
export const parseMeasurementPeriodEndYearMo = (label: string): string | null => {
  const normalized = normalizeWarehouseMeasurementPeriod(label);
  const parts = normalized.split(' - ').map(part => part.trim());
  if (parts.length < 2) {
    return null;
  }
  const end = parts[parts.length - 1] ?? '';
  return /^\d{6}$/.test(end) ? end : null;
};

const WAREHOUSE_MEASUREMENT_PERIOD_PATTERN = /^\d{6} - \d{6}$/;

const FULL_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const COMPACT_MONTH_NAMES = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
] as const;

/**
 * Returns true when a label matches warehouse `measurement_period` shape.
 * @param label - Report or customer measurement period label.
 * @returns Whether the normalized label is `YYYYMM - YYYYMM`.
 */
export const looksLikeWarehouseMeasurementPeriod = (label: string): boolean => {
  const normalized = normalizeWarehouseMeasurementPeriod(label);
  return WAREHOUSE_MEASUREMENT_PERIOD_PATTERN.test(normalized);
};

/**
 * Formats a warehouse `YYYYMM` token as `Mon YYYY`.
 * @param yearMo - Six-digit year-month such as `202604`.
 * @returns Short month label or the original token when not parseable.
 */
export const formatWarehouseYearMoLabel = (yearMo: string): string => {
  if (!/^\d{6}$/.test(yearMo)) {
    return yearMo;
  }
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ] as const;
  const year = yearMo.slice(0, 4);
  const monthIndex = Number.parseInt(yearMo.slice(4, 6), 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return yearMo;
  }
  return `${monthNames[monthIndex]} ${year}`;
};

/**
 * Formats a warehouse `YYYYMM` token with a full month name (e.g. `March 2027`).
 * @param yearMo - Six-digit year-month such as `202703`.
 * @returns Full month label or the original token when not parseable.
 */
export const formatWarehouseYearMoFullMonthLabel = (yearMo: string): string => {
  if (!/^\d{6}$/.test(yearMo)) {
    return yearMo;
  }
  const year = yearMo.slice(0, 4);
  const monthIndex = Number.parseInt(yearMo.slice(4, 6), 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return yearMo;
  }
  return `${FULL_MONTH_NAMES[monthIndex]} ${year}`;
};

/**
 * Formats a warehouse `YYYYMM` token as compact uppercase month + two-digit year.
 * @param yearMo - Six-digit year-month such as `202604`.
 * @returns Label such as `APR26`, or the original token when not parseable.
 */
export const formatWarehouseYearMoCompactLabel = (yearMo: string): string => {
  if (!/^\d{6}$/.test(yearMo)) {
    return yearMo;
  }
  const year = yearMo.slice(2, 4);
  const monthIndex = Number.parseInt(yearMo.slice(4, 6), 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) {
    return yearMo;
  }
  return `${COMPACT_MONTH_NAMES[monthIndex]}${year}`;
};

/**
 * Formats a warehouse measurement period as a compact uppercase range.
 * @param measurementPeriod - Report measurement period label.
 * @returns Label such as `APR26 - MAR27`, or the trimmed input when not parseable.
 */
export const formatMeasurementPeriodCompactRangeLabel = (
  measurementPeriod: string,
): string => {
  const normalized = normalizeWarehouseMeasurementPeriod(measurementPeriod.trim());
  if (!normalized) {
    return normalized;
  }
  const parts = normalized.split(' - ').map(part => part.trim());
  if (
    parts.length === 2 &&
    /^\d{6}$/.test(parts[0] ?? '') &&
    /^\d{6}$/.test(parts[1] ?? '')
  ) {
    return `${formatWarehouseYearMoCompactLabel(parts[0] ?? '')} - ${formatWarehouseYearMoCompactLabel(parts[1] ?? '')}`;
  }
  return normalized;
};

/**
 * Formats the ending month of a report measurement period for the hero eyebrow.
 * @param measurementPeriod - Report `measurementPeriod` or warehouse label.
 * @returns Label such as `March 2027`, or the trimmed input when not parseable.
 */
export const formatMeasurementPeriodEndHeroLabel = (
  measurementPeriod: string,
): string => {
  const trimmed = measurementPeriod.trim();
  if (!trimmed) {
    return trimmed;
  }
  const endYearMo = parseMeasurementPeriodEndYearMo(trimmed);
  if (endYearMo) {
    return formatWarehouseYearMoFullMonthLabel(endYearMo);
  }
  return trimmed;
};

/**
 * Builds the hero cover eyebrow (`Customer · Period`).
 * @param customerName - Report customer display name.
 * @param measurementPeriod - Report measurement period from settings.
 * @returns Eyebrow string before uppercase styling.
 */
export const buildHeroTagline = (
  customerName: string,
  measurementPeriod: string,
): string =>
  `${customerName.trim()} · ${formatMeasurementPeriodEndHeroLabel(measurementPeriod)}`;

/**
 * Formats a warehouse measurement period for UI display.
 * @param label - Raw `measurement_period` from BigQuery.
 * @returns Human-readable range such as `Apr 2026 – Mar 2027`.
 */
export const formatWarehouseMeasurementPeriodLabel = (label: string): string => {
  const normalized = normalizeWarehouseMeasurementPeriod(label);
  const parts = normalized.split(' - ').map(part => part.trim());
  if (
    parts.length === 2 &&
    /^\d{6}$/.test(parts[0] ?? '') &&
    /^\d{6}$/.test(parts[1] ?? '')
  ) {
    return `${formatWarehouseYearMoLabel(parts[0] ?? '')} – ${formatWarehouseYearMoLabel(parts[1] ?? '')}`;
  }
  return normalized;
};

/**
 * Formats a warehouse measurement period with full month names for report footers.
 * @param label - Raw `measurement_period` from BigQuery or stored report period.
 * @returns Human-readable range such as `February 2026 - January 2027`.
 */
export const formatWarehouseMeasurementPeriodFullMonthLabel = (
  label: string,
): string => {
  const normalized = normalizeWarehouseMeasurementPeriod(label.trim());
  if (!normalized) {
    return normalized;
  }
  const parts = normalized.split(' - ').map(part => part.trim());
  if (
    parts.length === 2 &&
    /^\d{6}$/.test(parts[0] ?? '') &&
    /^\d{6}$/.test(parts[1] ?? '')
  ) {
    return `${formatWarehouseYearMoFullMonthLabel(parts[0] ?? '')} - ${formatWarehouseYearMoFullMonthLabel(parts[1] ?? '')}`;
  }
  return normalized;
};

/**
 * Resolves the measurement period label to query in BigQuery.
 * @param reportingPeriod - Stored report reporting period.
 * @param measurementPeriods - Customer-configured JOC measurement periods.
 * @returns Normalized measurement period label for warehouse queries.
 */
export const resolveMeasurementPeriodForWarehouse = (
  reportingPeriod: string,
  measurementPeriods: string[] = [],
): string => {
  if (looksLikeWarehouseMeasurementPeriod(reportingPeriod)) {
    return normalizeWarehouseMeasurementPeriod(reportingPeriod);
  }
  const configured = measurementPeriods.find(period => period.trim().length > 0);
  if (configured) {
    return normalizeWarehouseMeasurementPeriod(configured);
  }
  return normalizeWarehouseMeasurementPeriod(reportingPeriod);
};

/**
 * Resolves the warehouse measurement period for a report instance.
 * @param params - Report cadence, stored periods, and optional explicit selection.
 * @returns Normalized measurement period for BigQuery queries.
 */
export const resolveReportWarehouseMeasurementPeriod = (params: {
  cadence: Cadence;
  reportingPeriod: string;
  measurementPeriod?: string | null;
  customerMeasurementPeriods?: string[];
}): string => {
  if (params.measurementPeriod?.trim()) {
    return normalizeWarehouseMeasurementPeriod(params.measurementPeriod);
  }
  if (params.cadence === 'adhoc') {
    return normalizeWarehouseMeasurementPeriod(params.reportingPeriod);
  }
  return resolveMeasurementPeriodForWarehouse(
    params.reportingPeriod,
    params.customerMeasurementPeriods ?? [],
  );
};
